var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import { Drawer } from '../drawer';
import { useTheme } from '../theme/use-theme';
import { styled } from '../styled';
import { useMediaQuery } from '../use-media-query';
import clsx from '../utils/clsx';
import { sidebarClasses, sidebarComponentName, getSidebarUtilityClass } from './sidebar-classes';
import { SidebarContext } from './sidebar-context';
import { useMergedClasses } from '../styles/classes';
var Root = styled(Drawer, { name: sidebarComponentName, slot: 'Root' })(function (_a) {
    var _b, _c, _d, _e;
    var theme = _a.theme, ownerState = _a.ownerState;
    var enterTransition = {
        easing: theme.transitions.easing.easeIn,
        duration: theme.transitions.duration.leavingScreen,
    };
    var leavingTransition = {
        easing: theme.transitions.easing.easeIn,
        duration: theme.transitions.duration.leavingScreen,
    };
    var top = theme.tokens.size_layout_app_top + theme.tokens.size_layout_app_bar_height;
    return _b = {
            top: top,
            zIndex: 20,
            marginRight: theme.layout.sidebarWidthCollapsed,
            transition: theme.transitions.create('margin', enterTransition),
            willChange: 'margin'
        },
        _b[theme.breakpoints.up('lg')] = {
            marginRight: ownerState.collapsed ? theme.layout.sidebarWidthCollapsed : theme.layout.sidebarWidth,
            transition: theme.transitions.create('margin', leavingTransition),
        },
        _b["& .".concat(sidebarClasses.paper)] = (_c = {
                top: top,
                overflowX: 'hidden',
                bottom: 0,
                height: 'auto',
                width: theme.layout.sidebarWidthCollapsed,
                padding: theme.spacing(3, 2),
                willChange: 'margin, box-shadow',
                transition: theme.transitions.create('width', enterTransition),
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
            },
            _c[theme.breakpoints.up('lg')] = {
                width: theme.layout.sidebarWidth,
            },
            _c["&.".concat(sidebarClasses.paperHovered)] = {
                boxShadow: '16px 0px 16px rgba(0, 0, 0, 0.1)',
                width: theme.layout.sidebarWidth,
                transition: theme.transitions.create(['width', 'box-shadow'], leavingTransition),
            },
            _c["&.".concat(sidebarClasses.paperCollapsed)] = (_d = {},
                _d[theme.breakpoints.up('lg')] = (_e = {
                        width: theme.layout.sidebarWidthCollapsed
                    },
                    _e["&.".concat(sidebarClasses.paperHovered)] = {
                        width: theme.layout.sidebarWidth,
                    },
                    _e),
                _d),
            _c),
        _b;
});
export var Sidebar = React.forwardRef(function (props, ref) {
    var _a = props.collapsed, collapsed = _a === void 0 ? false : _a, children = props.children, className = props.className, _b = props.PaperProps, PaperProps = _b === void 0 ? {} : _b, _c = props["aria-label"], ariaLabel = _c === void 0 ? 'primary navigation' : _c, propClasses = props.classes, drawerProps = __rest(props, ["collapsed", "children", "className", "PaperProps", 'aria-label', "classes"]);
    var classes = useMergedClasses(sidebarClasses, getSidebarUtilityClass, propClasses);
    var theme = useTheme();
    var isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
    var _d = __read(React.useState(false), 2), isHovered = _d[0], setHovered = _d[1];
    var shouldListenForHover = collapsed || !isLargeScreen;
    var expanded = collapsed ? isHovered : isLargeScreen || isHovered;
    var contextValue = React.useMemo(function () { return ({
        expanded: expanded,
        collapsed: !expanded,
        onCollapseChange: function () {
            // This is implemented in the new sidebar
        },
    }); }, [expanded]);
    var handleMouseOver = React.useCallback(function () {
        if (!isHovered) {
            setHovered(true);
        }
    }, [isHovered]);
    var handleMouseOut = React.useCallback(function () {
        if (isHovered) {
            setHovered(false);
        }
    }, [isHovered]);
    React.useEffect(function () {
        if (!shouldListenForHover && isHovered) {
            setHovered(false);
        }
    }, [isHovered, shouldListenForHover]);
    return (React.createElement(SidebarContext.Provider, { value: contextValue },
        React.createElement(Root, __assign({ ref: ref, ownerState: { collapsed: collapsed }, className: clsx(classes.root, className), anchor: "left", variant: "permanent", PaperProps: __assign(__assign(__assign({ component: 'nav' }, { 'aria-label': ariaLabel }), PaperProps), { className: clsx(classes.paper, collapsed && classes.paperCollapsed, shouldListenForHover && isHovered && classes.paperHovered, PaperProps.className) }), onMouseEnter: shouldListenForHover ? handleMouseOver : undefined, onMouseLeave: shouldListenForHover ? handleMouseOut : undefined }, drawerProps), children)));
});
